import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Form, Modal } from 'react-bootstrap';
import './AISuggestionsPanel.css';
import { getEventSearchSuggestions } from '../../helper';

// This component expects: 
// - vendor: vendor data object
// - weekData: { week, startDate, endDate }
// - onSelectionChange: function(selectedEventsArray)
const AISuggestedEventSelector = ({ vendor, weekData, onSelectionChange }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [formValues, setFormValues] = useState({
    workType: '',
    eventFocus: '',
    pricingPreference: '',
    eventScale: '',
  });

  

  const [selectedAIEvents, setSelectedAIEvents] = useState({});

  // Open/close modal for user preferences
  const handleOpenPrompt = () => setShowPromptModal(true);
  const handleClosePrompt = () => setShowPromptModal(false);

  // Update local form state
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  // Fetch AI suggestions
  const handleSubmitAndFetch = async () => {
    if (!vendor || !weekData.startDate || !weekData.endDate) return;

    setLoading(true);
    setSuggestions(null);
    setShowPromptModal(false);

    try {
      const truckDetails = {
        cuisines: vendor?.cuisine_tags || ['Unknown'],
        baseLocation: vendor?.hometown || 'Melbourne',
        travelRadiusKm: vendor?.travel_radius_km || 100,
        avgMealPrice: vendor?.average_meal_price || 18,
        dailySaleTarget: vendor?.target_daily_sales || 2500,
        maxEventsPerWeek: 4,
        eventTypePriority: formValues.eventFocus || vendor?.event_type_preference || 'Public',
        allowCatering: vendor?.catering_allowed ?? true,
        indoorPreferred: vendor?.indoor_preference ?? false,
        multiDayOk: vendor?.multi_day_ok ?? true,
        userPreferences: formValues,
      };

      const aiResult = await getEventSearchSuggestions({
        weekNumber: weekData.week,
        startDate: weekData.startDate,
        endDate: weekData.endDate,
        truckDetails,
      });

      setSuggestions({ success: true, content: aiResult });
      setSelectedAIEvents({}); // Reset previous selections
    } catch (err) {
      console.error('❌ Failed to fetch AI suggestions:', err);
      setSuggestions(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper: flatten events by location
  const getAllAISuggestedEvents = () => {
    if (!suggestions?.success || !suggestions?.content) return [];
    let parsed;
    try {
      parsed = typeof suggestions.content === "string"
        ? JSON.parse(suggestions.content)
        : suggestions.content;
    } catch (e) {
      return [];
    }
    if (!parsed?.locations) return [];
    return parsed.locations.flatMap(loc =>
      (loc.events || []).map(ev => ({
        ...ev,
        location: loc.name || ev.venue || "Unknown"
      }))
    );
  };

  // Checkbox handler
  const handleAIEventToggle = (eventKey) => {
    setSelectedAIEvents(prev => ({
      ...prev,
      [eventKey]: !prev[eventKey]
    }));
  };

  // Notify parent on selection change
  useEffect(() => {
    const allEvents = getAllAISuggestedEvents();
    const selectedEvents = allEvents.filter(ev => selectedAIEvents[getEventKey(ev)]);
    onSelectionChange && onSelectionChange(selectedEvents);
    // eslint-disable-next-line
  }, [selectedAIEvents, suggestions]);

  // Helper for unique event key
  const getEventKey = (event) => `${event.event_name}|${event.location}|${event.dates}`;

  // Optional: Group events by location for nicer UI
  const getGroupedByLocation = () => {
    if (!suggestions?.success || !suggestions?.content) return [];
    let parsed;
    try {
      parsed = typeof suggestions.content === "string"
        ? JSON.parse(suggestions.content)
        : suggestions.content;
    } catch (e) {
      return [];
    }
    return parsed.locations || [];
  };

  return (
    <>
      <Button variant="info" className="mb-3" onClick={handleOpenPrompt} disabled={loading}>
        {loading ? 'Loading Suggestions…' : 'Ask WTT AI for Event Suggestions'}
      </Button>

      {loading && <Spinner animation="border" variant="primary" className="ml-3" />}

      <Modal show={showPromptModal} onHide={handleClosePrompt}>
        <Modal.Header closeButton>
          <Modal.Title>Help us tailor your event suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="workType">
              <Form.Label>What do you like to work at?</Form.Label>
              <Form.Control
                type="text"
                name="workType"
                value={formValues.workType}
                placeholder="e.g. Late-night crowd, family markets"
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group controlId="eventFocus">
              <Form.Label>What kind of events do you focus on?</Form.Label>
              <Form.Control
                as="select"
                name="eventFocus"
                value={formValues.eventFocus}
                onChange={handleFormChange}
              >
                <option value="">Select one</option>
                <option value="Public">Public</option>
                <option value="Christmas Carols">Christmas Carols</option>
                <option value="Market Stalls">Market Stalls</option>
                <option value="Music Concerts">Music Concerts</option>
                <option value="Community Fairs">Community Fairs</option>
                <option value="Cultural Events">Cultural Events</option>
                <option value="All of the Above">All of the Above</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="pricingPreference">
              <Form.Label>What pricing style suits you?</Form.Label>
              <Form.Control
                as="select"
                name="pricingPreference"
                value={formValues.pricingPreference}
                onChange={handleFormChange}
              >
                <option value="">Select one</option>
                <option value="Low fee / Low revenue">Low fee / Low revenue</option>
                <option value="High fee / High revenue">High fee / High revenue</option>
                <option value="Balanced">Balanced</option>
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="eventScale">
              <Form.Label>Event scale preference?</Form.Label>
              <Form.Control
                as="select"
                name="eventScale"
                value={formValues.eventScale}
                onChange={handleFormChange}
              >
                <option value="">Select one</option>
                <option value="Major events only">Major events only</option>
                <option value="Minor events okay">Minor events okay</option>
                <option value="Let WTT decide">Let WTT decide</option>
                <option value="All of the above">All of the above</option>
              </Form.Control>
            </Form.Group>
            <Button variant="primary" onClick={handleSubmitAndFetch} disabled={loading}>
              Submit & Ask WTT AI to help you
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Show event selection UI if suggestions exist */}
      {suggestions?.success && (
        <div className="mt-4">
          <h5 className="mb-3 fw-bold text-secondary">Select Events from WTT AI Suggestions</h5>
          {getGroupedByLocation().length === 0 && (
            <p className="text-muted">No events suggested for this week.</p>
          )}
          {getGroupedByLocation().map((loc, idx) => (
            <Card key={idx} className="mb-3 shadow-sm border-light">
              <Card.Body>
                <Card.Title className="mb-3 text-info">{loc.name}</Card.Title>
                {(loc.events || []).length === 0 && (
                  <div className="text-muted small">No events found.</div>
                )}
                {(loc.events || []).map((event, evIdx) => {
                  const eventKey = getEventKey({ ...event, location: loc.name });
                  return (
                    <div key={evIdx} className="d-flex align-items-center justify-content-between border-bottom py-2">
                      <div>
                        <strong>{event.event_name}</strong>
                        <div className="text-muted">{event.dates} @ {loc.name}</div>
                        <div className="small">{event.description}</div>
                        {event.website && (
                          <a href={event.website} target="_blank" rel="noopener noreferrer" className="small">
                            More info
                          </a>
                        )}
                      </div>
                      <Form.Check
                        type="checkbox"
                        className="ms-3"
                        checked={!!selectedAIEvents[eventKey]}
                        onChange={() => handleAIEventToggle(eventKey)}
                        label="Include"
                      />
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

export default AISuggestedEventSelector;
